import React from 'react';

import { useResultsExportUrlState } from 'services/export/useResultsExportUrlState';
import { StartExport } from 'services/export/useSeparateOutputExports';
import { exportNormalizationValuesCsv } from 'utils/exports';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { ExportScopeField, ExportScopeValue } from './ExportFormFields';
import { ExportFormShell } from './ExportFormShell';
import { ResultsExportContext } from './useResultsExportContext';

interface NormalizationExportFormProps {
  context: ResultsExportContext;
  startExport: StartExport;
  isExportRunning: boolean;
  onBack: () => void;
  backLabel?: string;
}

export const NormalizationExportForm: React.FC<NormalizationExportFormProps> = ({
  context,
  startExport,
  isExportRunning,
  onBack,
  backLabel,
}) => {
  const { query, update } = useResultsExportUrlState();
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName(context.studyId);
  const hasSelection = context.hasIntermediateResultsSelection;
  const scopeOptions = [
    ...(hasSelection
      ? [{ value: 'selected', label: context.isSlidesMode ? 'Selected slides' : 'Selected cases' }]
      : []),
    ...(context.areFiltersAppliedExceptStudy ? [{ value: 'filtered', label: 'Current filtered dataset' }] : []),
    { value: 'whole', label: 'Whole study' },
  ];
  const scope = query.normalizationExportScope ?? scopeOptions[0].value;
  const validScope = scopeOptions.some((option) => option.value === scope);
  const guidance =
    !context.study?.allowExport || !context.studyId || !context.labId
      ? 'Exports are unavailable for this study.'
      : !validScope
      ? 'The previous scope is no longer available. Choose a scope.'
      : isLoadingStainTypeOptions
      ? 'Loading marker names...'
      : '';
  const casesParams =
    scope === 'selected'
      ? context.casesParamsWithoutPaginationAndSorting
      : scope === 'filtered'
      ? context.casesParamsWithoutSelections
      : { labId: context.labId, filters: { studyId: context.studyId } };

  return (
    <ExportFormShell
      study={context.study}
      scope={
        <ExportScopeField
          id="normalization-scope"
          label={context.isSlidesMode ? 'Slides' : 'Cases'}
          options={
            validScope
              ? scopeOptions
              : [{ value: scope, label: 'Previous scope (unavailable)', disabled: true }, ...scopeOptions]
          }
          value={scope}
          onChange={(value) => update({ normalizationExportScope: value })}
        />
      }
      options={<ExportScopeValue label="Output">Approved normalization min/max values (.csv)</ExportScopeValue>}
      primaryLabel="Download"
      primaryDisabled={Boolean(guidance) || isExportRunning}
      primaryGuidance={guidance}
      onPrimary={() => {
        if (guidance || isExportRunning) return;
        void startExport({
          title: 'Normalization values export',
          canCancel: true,
          run: async (signal) => {
            const count = await exportNormalizationValuesCsv({
              casesParams: casesParams as CasesParams,
              getMarkerLabel: stainTypeIdToDisplayName,
              fileNamePrefix: `studyId-${context.studyId}-${scope}-normalization`,
              signal,
            });
            if (!count) throw new Error('No slides with recorded channels were found in this scope.');
          },
        });
      }}
      onBack={onBack}
      backLabel={backLabel}
      downloadOnly
    />
  );
};
